/** Eligibility statuses that allow Edit / reopen of screening responses. */
export const SCREENING_EDIT_ELIGIBILITY = [
  "unverified",
  "decide_later",
  "included",
  "excluded",
] as const;

export function canEditScreeningByEligibility(
  eligibilityStatus?: string | null
): boolean {
  if (!eligibilityStatus) return true; // registry may not set this
  return (SCREENING_EDIT_ELIGIBILITY as readonly string[]).includes(
    eligibilityStatus
  );
}

/** Strict: every form in every screening phase must be completed (participant path). */
export function areAllScreeningFormsCompleted(
  phaseList: Array<{ phaseForms?: Array<{ status?: string }> }>
): boolean {
  if (!phaseList?.length) return false;
  for (const phase of phaseList) {
    const forms = phase.phaseForms || [];
    if (forms.length === 0) return false;
    for (const form of forms) {
      if (form.status !== "completed") return false;
    }
  }
  return true;
}

export type ScreeningPhaseFormForIncomplete = {
  id?: string;
  name?: string;
  status?: string;
  requiredQuestions?: number | null;
  requiredAnswered?: number | null;
};

export type IncompleteScreeningForm = {
  formId: string;
  name: string;
  missingRequiredCount: number;
};

export function getMissingRequiredCount(
  form: ScreeningPhaseFormForIncomplete
): number {
  const required = Number(form.requiredQuestions ?? 0);
  const answered = Number(form.requiredAnswered ?? 0);
  return Math.max(0, required - answered);
}

/** Forms with unanswered required questions only (blank optional-only is not incomplete). */
export function getIncompleteScreeningForms(
  phaseList: Array<{ phaseForms?: ScreeningPhaseFormForIncomplete[] }>
): IncompleteScreeningForm[] {
  const result: IncompleteScreeningForm[] = [];
  for (const phase of phaseList || []) {
    for (const form of phase.phaseForms || []) {
      const missingRequiredCount = getMissingRequiredCount(form);
      if (missingRequiredCount > 0) {
        result.push({
          formId: form.id || "",
          name: form.name || "Untitled form",
          missingRequiredCount,
        });
      }
    }
  }
  return result;
}

export function summarizeIncompleteScreening(
  phaseList: Array<{ phaseForms?: ScreeningPhaseFormForIncomplete[] }>
): {
  totalMissingRequired: number;
  formCount: number;
  forms: IncompleteScreeningForm[];
} {
  const forms = getIncompleteScreeningForms(phaseList);
  return {
    forms,
    formCount: forms.length,
    totalMissingRequired: forms.reduce(
      (sum, f) => sum + f.missingRequiredCount,
      0
    ),
  };
}
